<template>
  <div class="min-h-screen">
    <!--
      The wrappers below are load-bearing. `pwaInstalled` is false during SSR
      and the first client render, then flips in onMounted (see
      composables/use-pwa-installed.js). Mounting or unmounting a DIRECT
      SIBLING of the still-suspended <NuxtPage> re-anchors the insert point,
      so when Suspense resolves the page mount fails with "insertBefore: The
      node before which the new node is to be inserted is not a child of this
      node" and every dashboard route errors on reload inside the installed
      PWA. An always-rendered wrapper keeps the chrome swapping INSIDE an
      attached element, so the page's anchor never moves. Do not inline these
      v-ifs back as siblings of the slot.
    -->
    <!-- Sidebar -->
    <div>
      <OrganismsDashboardSidebar v-if="!pwaInstalled" />
    </div>
    <!-- Main Content Area -->
    <div class="flex-1 pt-safe">
      <!-- Smart App Banner (Android + iOS non-Safari) -->
      <V2AtomsSmartAppBanner />
      <!-- Header -->
      <!-- Sits below the Smart App Banner, which publishes its height as
           --smart-app-banner-height (0px when there is no banner). -->
      <OrganismsDashboardHeader
        v-if="!pwaInstalled"
        style="top: var(--smart-app-banner-height)"
        class="max-sm:transition-[top] max-sm:duration-300"
      />
      <OrganismsNavPwaMobileNav v-if="pwaInstalled" />
      <!-- Page Content -->
      <div
        class="mx-auto max-w-(--breakpoint-2xl) overflow-x-clip transition-all duration-300 ease-in-out bg-semantic-surface-container-variant-low"
        :class="{
          'lg:ml-[290px]': !pwaInstalled && !hideSidebarOnDesktop && (isExpanded || isHovered),
          'lg:ml-[88px]': !pwaInstalled && !hideSidebarOnDesktop && !(isExpanded || isHovered)
        }"
        :style="[
          pwaInstalled && !hideDashboardMenu ? { paddingBottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' } : undefined,
          { paddingTop: 'calc(4rem + var(--smart-app-banner-height))' }
        ]"
      >
        <slot />
      </div>

      <div
        v-if="pwaInstalled && !hideDashboardMenu"
        id="pwa-nav-bar"
        class="fixed w-full bottom-0 z-[2147483000]">
        <div
          class="text-2xs content-center text-center text-white transition-opacity duration-1000 ease-in-out font-bold"
          :class="{
            'bg-blue-900 opacity-100 h-[1.25rem]': !isOnline,
            'bg-blue-500 opacity-100 h-[1.25rem]': isOnline && showOnlineMessage,
            'opacity-0 h-0': isOnline && !showOnlineMessage
          }"
        >
          {{ isOnline ? 'Back Online' : 'No Connection' }}
        </div>
        <ul class="flex flex-wrap text-sm font-medium text-center text-gray-500 dark:text-gray-400 justify-between min-h-[calc(5rem+env(safe-area-inset-bottom,0px))] border-black/10 bg-white pb-safe" :class="{'border-t':isOnline && !showOnlineMessage}">
          <li
            v-for="tab in tabs"
            :key="tab.title"
            :style="{ width: `${100 / tabs.length}%` }">
            <nuxt-link
              exact-active-class="font-bold text-semantic-secondary-default"
              :to="tab.route"
              class="inline-flex flex-col items-center justify-start pt-2 px-1 border-t-2 border-transparent text-semantic-primary-variant-dark group w-full h-full"
              @click="onTabClick(tab)">
              <component
                :is="tab.icon"
                fill="currentColor"
                class="w-6 h-6"/>
              <span class="mt-1 text-[11px] leading-tight whitespace-nowrap">{{ tab.title }}</span>
            </nuxt-link>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Capacitor } from '@capacitor/core'
import { FEATURE_FLAG } from '~/constants/feature-flags.constants'
import isFlagOn from '~/composables/feature-flag'
import { isInternalEmail } from '~/composables/ai-advisor-access'
import { trackChatMenu } from '~/otto/analytics'
const { isExpanded, isHovered, hideSidebarOnDesktop, setHideSidebarOnDesktop } = useSidebar()
const userStore = useUserStore()
const vaultStore = useVaultStore()

// ── iOS Smart App Banner ──────────────────────────────────────────────────
// Inject the native Safari Smart App Banner only on dashboard pages when
// the app is NOT already installed as a PWA/native app.
// App Store ID: 6752555566  |  bundle: com.compareclub.app
useHead({
  meta: [
    {
      name: 'apple-itunes-app',
      // app-argument deep-links into the current dashboard path inside the app
      content: 'app-id=6752555566, app-argument=https://compareclub.com.au/dashboard/'
    }
  ]
})

const { $bus, $tracking } = useNuxtApp()

defineOptions({
  name: 'MemberHubLayout'
})

useOneTap()

const route = useRoute()
const router = useRouter()

watch(route, () => {
  if (
    (['dashboard-vault-product-vertical-completion'].includes(route.name) && route.query.source === 'product-upload')
    || route.name?.includes('dashboard-vault-product-form-product')
    // CLUB-919: only the generic form's step 1 is the full-width category picker. Its
    // form steps (2+) are ordinary vault forms and keep the sidebar, like energy/health.
    || (route.name?.includes('dashboard-vault-product-form-generic') && String(route.params.step) === '1')
    || route.name?.includes('dashboard-quick-compare-health')
    || route.name?.includes('dashboard-quick-compare-home-loan')
    || route.name?.includes('dashboard-quick-compare-life')
    || route.name?.includes('dashboard-quick-compare-energy')
    || route.name?.includes('dashboard-quick-compare-travel-insurance')
    || route.name?.includes('dashboard-energy-cdr-connect')
    || route.name?.includes('dashboard-energy-cdr-thank-you')
  ) {
    setHideSidebarOnDesktop(true)
  } else {
    setHideSidebarOnDesktop(false)
  }
}, { deep: true, immediate: true })
const hideDashboardMenu = computed(() => {
  return !isDashboardPage.value
})
const isDashboardPage = computed(() => {
  return ['dashboard','dashboard-quotes','dashboard-rewards','dashboard-perks'].includes(route.name)
})

// AI Advisor (OTTO) nav item is gated by the feature_AIAdvisor Split.io flag,
// resolved client-side on mount (see onMounted below), OR a compareclub.com.au
// member email (CLUB-949, internal production testing).
const isAIAdvisorFlagOn = ref(false)
const isAIAdvisorEnabled = computed(() =>
  isAIAdvisorFlagOn.value || isInternalEmail(userStore.getUserProfile?.email)
)
const isPageRendered = ref(false)
// App chrome selection. usePwaInstalled() seeds from the build-time app target, so the app
// build paints the app header/bottom-nav from the first render and never mounts the desktop
// header/sidebar — avoiding the post-hydration swap flash (and the double Braze init it
// caused). On the web build it stays false at SSR and resolves the installed web-PWA case on
// mount, matching the desktop default.
const pwaInstalled = usePwaInstalled()

const waitForIntercom = () => {
  return new Promise(resolve => {
    const interval = setInterval(() => {
      if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
        clearInterval(interval)
        resolve()
      }
    }, 500)
  })
}

onMounted(async () => {
  isPageRendered.value = true
  // Resolve the AI Advisor flag (default off on any SDK error/timeout).
  isFlagOn(FEATURE_FLAG.aiAdvisor).then(on => {
    isAIAdvisorFlagOn.value = on
  }).catch(() => {
    // default off on SDK error/timeout
  })
  nextTick().then(() => {
    if(!vaultStore?.verticals?.length) {
      // The result is never consumed here, so an unhandled rejection reached
      // the console whenever the call failed - notably when the
      // api-interceptor aborts a duplicate in-flight /data/verticals.
      vaultStore.fetchVerticals().catch(() => undefined)
    }
    $bus.off('add-vault-product')
    $bus.on('add-vault-product', addProductHandler)
  })
  // Hide intercom launcher
  if (pwaInstalled.value || Capacitor.isNativePlatform()) {
    await waitForIntercom()
    window.Intercom('update', { hide_default_launcher: true })
  }
})

const emailVerified = computed(() => {
  return userStore.isEmailVerified
})
const isGuestUser = computed(() => {
  return userStore.isGuestUser
})

const { isOnline,showOnlineMessage } = useOnlineStatus()

// Resolve icon components once at setup (render-safe), then reference them.
const IconHome = resolveComponent('V2IconsHome')
const IconClipboard = resolveComponent('V2IconsClipboard')
const IconPartyPopper = resolveComponent('V2IconsPartyPopper')
const IconGift = resolveComponent('V2IconsGift')
const IconAiAdvisor = resolveComponent('V2IconsAiAdvisor')

// AI Adviser sits in the middle. When the flag is on the array has 5 items so
// it lands at index 2 (true centre); when off it falls back to the original 4.
const tabs = computed(() => {
  const items = [
    { title: 'Dashboard', route: '/dashboard/', icon: IconHome },
    { title: 'Quotes', route: '/dashboard/quotes/', icon: IconClipboard },
    { title: 'Gift Cards', route: '/dashboard/rewards/', icon: IconPartyPopper },
    { title: 'Perks', route: '/dashboard/perks/', icon: IconGift }
  ]
  if (isAIAdvisorEnabled.value) {
    items.splice(2, 0, {
      title: 'AI Adviser',
      route: '/dashboard/ai-advisor/',
      icon: IconAiAdvisor,
      // chat_menu (Henry AI Advisor Energy Tracking plan)
      chatMenu: true
    })
  }
  return items
})

function onTabClick (tab) {
  if (tab.chatMenu) trackChatMenu($tracking, 'bottom_nav')
}

function resendEmail () {
  if(isGuestUser.value) {
    userStore.guestPasswordResetHandler({ formData:{ email: userStore.getUserProfile?.email } })
    return
  }
  userStore.resendVerification()
}

usePwaUpdate()

function addProductHandler () {
  router.push({ name:'dashboard-vault-product-form-generic-step-step', params:{ step:1 } })
}

</script>